/**
 * Live Video — Fanatic variant (FR-VIDEO-06, AC-FANATIC-005/006, AC-VIDEO-006).
 *
 * The embed and the stat strip are independent: whether or not the team's video
 * provider lets spectators watch, the score/possession/team-totals strip keeps
 * updating. That is the whole point of the fail-soft state — a Hudl tenant's
 * fans still get the game, just without the picture.
 *
 * | Provider `fanatic_visible` | What a Fanatic sees            |
 * | -------------------------- | ------------------------------ |
 * | `true`  (YouTube)          | sandboxed iframe embed         |
 * | `false` (Hudl) → 403       | neutral "not available" card    |
 * | not configured → 404       | neutral "no video yet" card     |
 *
 * The copy is deliberately product-level — never a licensing lecture.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { readEmbedFailure, VideoEmbed } from '../../components/video-embed';
import type { VideoEmbedFailReason } from '../../components/video-embed';
import type { GameSummaryFeed, VideoEmbedInfo } from '../../lib/api-types';
import { FANATIC_VIEWS } from '../../lib/constants';
import { formatStatValue } from '../summary/stat-block';

import { toFanaticMessage, useFanaticPortal } from './fanatic-portal';

/** Live cadence for the spectator strip. */
const STRIP_REFRESH_MS = 10_000;

const FAIL_COPY: Record<VideoEmbedFailReason, { title: string; body: string }> = {
  fanatic_restricted: {
    title: 'Video unavailable',
    body: 'This stream is not available to spectators. Follow the live game stats below — they update as plays are logged.',
  },
  plan_excluded: {
    title: 'Video unavailable',
    body: 'Video is not included in this team\u2019s plan. Follow the live game stats below — they update as plays are logged.',
  },
  not_configured: {
    title: 'Video unavailable',
    body: 'This team has not set up a video stream yet. The live game stats below still update as plays are logged.',
  },
  not_available: {
    title: 'Video unavailable',
    body: 'There is no video for this game yet. The live game stats below still update as plays are logged.',
  },
  error: {
    title: 'Video unavailable',
    body: 'The stream could not be loaded right now. The live game stats below still update as plays are logged.',
  },
};

type EmbedState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; embed: VideoEmbedInfo }
  | { kind: 'failed'; reason: VideoEmbedFailReason };

interface StripItem {
  label: string;
  value: string;
}

function possessionLabel(possession: string | null): string {
  if (possession === null) return '—';
  if (possession === 'offense') return 'Our ball';
  if (possession === 'defense') return 'Opponent ball';
  return 'Special teams';
}

function buildStrip(feed: GameSummaryFeed | null, teamName: string): StripItem[] {
  if (feed === null) {
    return [
      { label: 'Score', value: '—' },
      { label: 'Quarter', value: '—' },
      { label: 'Clock', value: '—' },
      { label: 'Possession', value: '—' },
    ];
  }
  const general = feed.statistics.general;
  const offense = feed.statistics.offense ?? {};
  return [
    {
      label: `${teamName} — Opponent`,
      value: `${general.total_score.team}–${general.total_score.opponent}`,
    },
    { label: 'Quarter', value: general.quarter > 0 ? `Q${general.quarter}` : '—' },
    { label: 'Clock', value: general.game_clock ?? '—' },
    { label: 'Possession', value: possessionLabel(general.possession) },
    {
      label: 'Passing yards',
      value: formatStatValue(offense.passing_yards, 'yards'),
    },
    {
      label: 'Rushing yards',
      value: formatStatValue(offense.rushing_yards, 'yards'),
    },
    {
      label: 'Completion %',
      value: formatStatValue(offense.completion_percent, 'percent'),
    },
  ];
}

export function FanaticLiveVideoPage(): JSX.Element {
  const apiClient = useApiClient();
  const { activeGrant, isLoading: isPortalLoading, tenantId, withTeam } = useFanaticPortal();
  const { activeEvent, activeEventId } = useActiveEvent();
  const [embedState, setEmbedState] = useState<EmbedState>({ kind: 'idle' });
  const [feed, setFeed] = useState<GameSummaryFeed | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const teamName = activeGrant?.team_name ?? 'Your team';
  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  // The embed is resolved once per event: a 403 here is the gate, not a bug.
  useEffect(() => {
    if (activeEventId === null || tenantId === null) {
      setEmbedState({ kind: 'idle' });
      return undefined;
    }
    const controller = new AbortController();
    setEmbedState({ kind: 'loading' });
    void (async () => {
      try {
        const embed = await apiClient.getVideoEmbed(activeEventId, controller.signal);
        if (typeof embed?.embed_url !== 'string' || embed.embed_url === '') {
          setEmbedState({ kind: 'failed', reason: 'not_available' });
          return;
        }
        setEmbedState({ kind: 'ready', embed });
      } catch (cause) {
        if (controller.signal.aborted) return;
        setEmbedState({ kind: 'failed', reason: readEmbedFailure(cause) });
      }
    })();
    return () => controller.abort();
  }, [activeEventId, apiClient, reloadToken, tenantId]);

  useEffect(() => {
    if (activeEventId === null || tenantId === null) {
      setFeed(null);
      return undefined;
    }
    const controller = new AbortController();
    void (async () => {
      try {
        const loaded = await apiClient.getGameSummary(activeEventId, {
          unit: 'all',
          signal: controller.signal,
        });
        setFeed(loaded);
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(toFanaticMessage(cause, 'We could not load the live game stats.'));
      }
    })();
    return () => controller.abort();
  }, [activeEventId, apiClient, reloadToken, tenantId]);

  useEffect(() => {
    if (activeEventId === null) return undefined;
    const timer = setInterval(reload, STRIP_REFRESH_MS);
    return () => clearInterval(timer);
  }, [activeEventId, reload]);

  if (isPortalLoading) return <p className="fan-empty">Loading…</p>;

  if (activeGrant === null) {
    return (
      <section className="fan-panel">
        <h2 className="fan-panel__title">Follow a team first</h2>
        <p className="fan-panel__hint">
          Live video and live stats are scoped to a team you follow.
        </p>
        <div className="fan-panel__actions">
          <Link className="btn btn--primary" to={withTeam(FANATIC_VIEWS.browse)}>
            Find teams
          </Link>
        </div>
      </section>
    );
  }

  if (activeEventId === null) {
    return (
      <section className="fan-panel">
        <h2 className="fan-panel__title">Choose a game</h2>
        <p className="fan-panel__hint">Pick a game to watch and follow along live.</p>
        <div className="fan-panel__actions">
          <Link className="btn btn--primary" to={withTeam(FANATIC_VIEWS.home)}>
            See {teamName}’s games
          </Link>
        </div>
      </section>
    );
  }

  const strip = buildStrip(feed, teamName);

  return (
    <section className="fan-panel" aria-label={`Live video for ${teamName}`}>
      <div className="fan-panel__head">
        <h2 className="fan-panel__title">{activeEvent?.name ?? 'Live video'}</h2>
        <button className="btn btn--ghost btn--sm" onClick={reload} type="button">
          Refresh
        </button>
      </div>

      <div className="fan-video-slot" data-testid="fanatic-live-video-slot">
        {embedState.kind === 'loading' || embedState.kind === 'idle' ? (
          <p className="fan-empty" role="status">
            Loading video…
          </p>
        ) : null}

        {embedState.kind === 'ready' ? (
          <VideoEmbed
            embed={embedState.embed}
            eventId={activeEventId}
            title={`${activeEvent?.name ?? 'Game'} live video`}
          />
        ) : null}

        {embedState.kind === 'failed' ? (
          <div className="fan-video-fallback" data-testid="video-unavailable" role="status">
            <p className="fan-video-fallback__title">{FAIL_COPY[embedState.reason].title}</p>
            <p className="fan-video-fallback__body">{FAIL_COPY[embedState.reason].body}</p>
            {embedState.reason === 'error' ? (
              <button className="btn btn--secondary btn--sm" onClick={reload} type="button">
                Try again
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}{' '}
          <button className="btn btn--ghost btn--sm" onClick={reload} type="button">
            Try again
          </button>
        </p>
      ) : null}

      <div aria-label="Live team stats" className="fan-strip" data-testid="fanatic-stat-strip">
        {strip.map((item) => (
          <div className="fan-strip__item" key={item.label}>
            <span className="fan-strip__label">{item.label}</span>
            <span className="fan-strip__value">{item.value}</span>
          </div>
        ))}
      </div>

      <p className="fanatic__aggregate-note">
        Team totals only — individual player stats stay with the coaching staff.
      </p>

      <div className="fan-panel__actions">
        <Link className="btn btn--secondary" to={withTeam(FANATIC_VIEWS.gameSummary)}>
          Game summary
        </Link>
        <Link className="btn btn--ghost" to={withTeam(FANATIC_VIEWS.home)}>
          All games
        </Link>
      </div>
    </section>
  );
}
